/**
 * Per-customer engagement and sales metrics on a salon customer list row
 * (matches the `performance` field on rows from `DashboardQueryService.getCustomers`).
 *
 * @typedef {Object} SalonCustomerPerformance
 * @property {number} salesAmount
 * @property {number} sentStyleCount
 * @property {number} viewedStyleCount
 * @property {number} favoriteCount
 * @property {number} addToCartCount
 * @property {number} purchaseCount
 */

/**
 * Maps raw salon customer records into view models for the salon customer list.
 */
export default class CustomerMapper {
  /**
   * Converts a customer record into the shape expected by the list row component.
   *
   * @param {Object} customer Source customer data (e.g. from the dashboard query API).
   * @param {string|number} customer.customerId
   * @param {string[]} [customer.stylistNames]
   * @param {string|number|Date} customer.lastVisitedAt Value passed to the {@link Date} constructor.
   * @param {boolean} [customer.hasPurchased]
   * @param {SalonCustomerPerformance} [customer.performance]
   * @returns {{
   *   customerId: string|number,
   *   name: string,
   *   stylistNames: string[]|undefined,
   *   lastVisitedAt: Date,
   *   hasPurchased: boolean|undefined,
   *   performance: SalonCustomerPerformance|undefined,
   *   history: Array,
   *   expanded: boolean,
   *   processing: boolean,
   * }} Row view model; `name` and `history` start empty and may be filled later.
   */
  static toViewModel(customer) {
    return {
      customerId: customer.customerId,
      name: '',
      stylistNames: customer.stylistNames,
      lastVisitedAt: new Date(customer.lastVisitedAt),
      hasPurchased: customer.hasPurchased,
      performance: customer.performance,
      history: [],
      expanded: false,
      processing: false,
    };
  }
}